import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Saint {
  id: string;
  name: string;
  title: string;
  feastDay: string;
  patronOf: string[];
  image: string;
  alt: string;
  description: string;
}

const featuredSaints: Saint[] = [
{
  id: 'st_anthony',
  name: 'St. Anthony of Padua',
  title: 'Finder of Lost Things',
  feastDay: 'June 13',
  patronOf: ['Lost Items', 'Lost People', 'Travelers'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1432c75e7-1765689274474.png",
  alt: 'St. Anthony of Padua in Franciscan robes holding infant Jesus',
  description: 'Known for miracles and finding lost items'
},
{
  id: 'st_lucy',
  name: 'St. Lucy',
  title: 'Light in Darkness',
  feastDay: 'December 13',
  patronOf: ['Eyes', 'Blindness', 'Light'],
  image: "https://images.unsplash.com/photo-1660238264686-b022f939c17a",
  alt: 'St. Lucy with crown of candles and gentle expression',
  description: 'Martyr who brought light to early Christians'
},
{
  id: 'st_christopher',
  name: 'St. Christopher',
  title: 'Protector of Travelers',
  feastDay: 'July 25',
  patronOf: ['Travelers', 'Drivers', 'Sailors'],
  image: "https://images.unsplash.com/photo-1627697893322-cd92f708acf9",
  alt: 'St. Christopher carrying child across river with staff',
  description: 'Carried Christ across the river'
},
{
  id: 'st_cecilia',
  name: 'St. Cecilia',
  title: 'Patron of Musicians',
  feastDay: 'November 22',
  patronOf: ['Musicians', 'Singers', 'Poets'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_172f09623-1770044974371.png",
  alt: 'St. Cecilia playing organ with angelic expression',
  description: 'Sang to God even in martyrdom'
},
{
  id: 'st_jude',
  name: 'St. Jude Thaddeus',
  title: 'Patron of Hope',
  feastDay: 'October 28',
  patronOf: ['Impossible Cases', 'Lost Causes', 'Hope'],
  image: "https://images.unsplash.com/photo-1617049036813-73266c1408bd",
  alt: 'St. Jude Thaddeus with staff and flame above head',
  description: 'Helper in desperate situations'
},
{
  id: 'st_michael',
  name: 'St. Michael the Archangel',
  title: 'Defender Against Evil',
  feastDay: 'September 29',
  patronOf: ['Police', 'Military', 'Protection'],
  image: "https://images.unsplash.com/photo-1620213835551-ecd03fe36475",
  alt: 'St. Michael Archangel with sword and armor defeating dragon',
  description: 'Leader of the army of God'
}];


export default function FeaturedSaintsSection() {
  return (
    <section className="py-24 px-6 bg-muted/30 reveal">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
            Featured Saints
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
            Meet the Saints
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover the lives and intercession of these beloved saints
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[400px]">
          {/* Large Card - Spans 2 columns and 2 rows */}
          <Link
            href={`/individual-saint?saint=${featuredSaints[0].id}`}
            className="md:col-span-8 row-span-2 group relative overflow-hidden rounded-3xl glass-card hover:shadow-2xl transition-all duration-300">
            
            <AppImage
              src={featuredSaints[0].image}
              alt={featuredSaints[0].alt}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="flex flex-wrap gap-2 mb-3">
                {featuredSaints[0].patronOf.slice(0, 3).map((patron) =>
                <span
                  key={patron}
                  className="text-xs bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full">
                  
                    {patron}
                  </span>
                )}
              </div>
              <h3 className="text-3xl font-serif font-bold text-white mb-2">
                {featuredSaints[0].name}
              </h3>
              <p className="text-white/80 text-sm mb-1">{featuredSaints[0].title}</p>
              <p className="text-white/60 text-xs">Feast Day: {featuredSaints[0].feastDay}</p>
            </div>
          </Link>

          {/* Medium Card 1 */}
          <Link
            href={`/individual-saint?saint=${featuredSaints[1].id}`}
            className="md:col-span-4 group relative overflow-hidden rounded-3xl glass-card hover:shadow-2xl transition-all duration-300">
            
            <AppImage
              src={featuredSaints[1].image}
              alt={featuredSaints[1].alt}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-xl font-serif font-bold text-white mb-1">
                {featuredSaints[1].name}
              </h3>
              <p className="text-white/80 text-xs">{featuredSaints[1].feastDay}</p>
            </div>
          </Link>

          {/* Medium Card 2 */}
          <Link
            href={`/individual-saint?saint=${featuredSaints[2].id}`}
            className="md:col-span-4 group relative overflow-hidden rounded-3xl glass-card hover:shadow-2xl transition-all duration-300">
            
            <AppImage
              src={featuredSaints[2].image}
              alt={featuredSaints[2].alt}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-xl font-serif font-bold text-white mb-1">
                {featuredSaints[2].name}
              </h3>
              <p className="text-white/80 text-xs">{featuredSaints[2].feastDay}</p>
            </div>
          </Link>

          {/* Small Cards Row */}
          {featuredSaints.slice(3, 6).map((saint) =>
          <Link
            key={saint.id}
            href={`/individual-saint?saint=${saint.id}`}
            className="md:col-span-4 group relative overflow-hidden rounded-3xl glass-card hover:shadow-2xl transition-all duration-300">
            
              <AppImage
              src={saint.image}
              alt={saint.alt}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-lg font-serif font-bold text-white mb-1">
                  {saint.name}
                </h3>
                <p className="text-white/80 text-xs">{saint.feastDay}</p>
              </div>
            </Link>
          )}
        </div>

        {/* View All Link */}
        <div className="text-center mt-12">
          <Link
            href="/search-saints"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
            
            <span>Explore All Saints</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>);

}